import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setError("No verification token found");
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await api.post("/auth/verify", { token });
        login(response.data.token, response.data.email);
        setStatus("success");
        toast.success("Successfully signed in!");
        setTimeout(() => navigate("/"), 1500);
      } catch (error) {
        setStatus("error");
        setError(error.response?.data?.detail || "Verification failed. The link may have expired.");
      }
    };

    verifyToken();
  }, [searchParams, login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-none">
        <CardContent className="pt-8 pb-8">
          <div className="text-center">
            {/* Logo */}
            <img 
              src="https://customer-assets.emergentagent.com/job_50f5a9a4-7203-4eda-9166-5e288543e67f/artifacts/9ya4uhxn_PointeCaptiallargeLogo.png" 
              alt="Pointe Capital Management" 
              className="h-12 mx-auto mb-6"
            />

            {status === "verifying" && (
              <>
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="h-8 w-8 text-[#8B0000] animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Verifying...</h3>
                <p className="text-slate-600">Please wait while we verify your sign-in link.</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Success!</h3>
                <p className="text-slate-600">Redirecting you to the dashboard...</p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Verification Failed</h3>
                <p className="text-slate-600 mb-6">{error}</p>
                <Button 
                  onClick={() => navigate("/login")}
                  className="btn-primary"
                  data-testid="back-to-login-btn"
                >
                  Back to Sign In
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
